'use client';

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PlayCircle, Layers, Loader2 } from 'lucide-react';
import { RoleContextSelector } from './RoleContextSelector';
import { SimulationDiagnostics, SimulationResult } from './SimulationDiagnostics';
import { BatchScenarioTester, TestScenario } from './BatchScenarioTester';

export interface SimulationRequest {
  principal: string;
  action: string;
  resource: string;
  roles?: string[];
  context?: Record<string, unknown>;
}

export interface EnhancedSimulatorProps {
  onSimulate: (request: SimulationRequest) => Promise<SimulationResult>;
  availableRoles?: string[];
  availableResources?: string[];
  availableActions?: string[];
}

export function EnhancedSimulator({
  onSimulate,
  availableRoles = ['admin', 'manager', 'developer', 'member', 'viewer', 'billing'],
  availableResources = ['Matricks::Workflow', 'Matricks::Contact', 'Matricks::Provider', 'Matricks::AgentPipeline'],
  availableActions = ['read', 'write', 'delete', 'execute']
}: EnhancedSimulatorProps) {
  // Single test state
  const [principal, setPrincipal] = useState('');
  const [action, setAction] = useState('read');
  const [resource, setResource] = useState('Matricks::Workflow');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<SimulationResult | null>(null);

  // Batch test state
  const [scenarios, setScenarios] = useState<TestScenario[]>([]);
  const [isBatchRunning, setIsBatchRunning] = useState(false);

  const handleSingleTest = async () => {
    if (!principal) return;

    setIsLoading(true);
    setResult(null);

    try {
      const request: SimulationRequest = {
        principal,
        action,
        resource,
        roles: selectedRoles.length > 0 ? selectedRoles : undefined
      };

      const simResult = await onSimulate(request);
      setResult(simResult);
    } catch (error) {
      setResult({
        allowed: false,
        reason: 'Simulation failed: ' + (error instanceof Error ? error.message : 'Unknown error'),
        errors: [String(error)]
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBatchRunAll = async () => {
    setIsBatchRunning(true);

    for (const scenario of scenarios) {
      const request: SimulationRequest = {
        principal: scenario.principal,
        action: scenario.action,
        resource: scenario.resource,
        roles: scenario.roles
      };

      try {
        const result = await onSimulate(request);
        const actualResult = result.allowed ? 'allow' : 'deny';
        const passed = actualResult === scenario.expectedResult;

        setScenarios(prev =>
          prev.map(s =>
            s.id === scenario.id
              ? { ...s, actualResult, status: passed ? 'passed' : 'failed' }
              : s
          )
        );
      } catch {
        setScenarios(prev =>
          prev.map(s =>
            s.id === scenario.id
              ? { ...s, status: 'failed' }
              : s
          )
        );
      }
    }

    setIsBatchRunning(false);
  };

  const handleRunSingle = async (scenario: TestScenario): Promise<{ allowed: boolean }> => {
    const request: SimulationRequest = {
      principal: scenario.principal,
      action: scenario.action,
      resource: scenario.resource,
      roles: scenario.roles
    };

    const result = await onSimulate(request);
    return { allowed: result.allowed };
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="single" className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-[400px]">
          <TabsTrigger value="single">
            <PlayCircle className="h-4 w-4 mr-2" />
            Single Test
          </TabsTrigger>
          <TabsTrigger value="batch">
            <Layers className="h-4 w-4 mr-2" />
            Batch Test
          </TabsTrigger>
        </TabsList>

        {/* Single Test Tab */}
        <TabsContent value="single" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Authorization Test</CardTitle>
              <CardDescription>
                Test whether a user can perform a specific action on a resource
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Role Context */}
              <RoleContextSelector
                selectedRoles={selectedRoles}
                availableRoles={availableRoles}
                onRolesChange={setSelectedRoles}
              />

              {/* Test Parameters */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Principal (User)
                  </label>
                  <Input
                    placeholder="e.g., alice@example.com"
                    value={principal}
                    onChange={e => setPrincipal(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Action</label>
                  <Select value={action} onValueChange={setAction}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableActions.map(a => (
                        <SelectItem key={a} value={a}>{a}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Resource</label>
                  <Select value={resource} onValueChange={setResource}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableResources.map(r => (
                        <SelectItem key={r} value={r}>
                          {r.split('::')[1] || r}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Run Button */}
              <Button
                onClick={handleSingleTest}
                disabled={!principal || isLoading}
                className="w-full md:w-auto"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <PlayCircle className="h-4 w-4 mr-2" />
                )}
                Run Simulation
              </Button>

              {/* Results */}
              {result && (
                <SimulationDiagnostics
                  result={result}
                  request={{
                    principal,
                    action,
                    resource,
                    roles: selectedRoles.length > 0 ? selectedRoles : undefined
                  }}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Batch Test Tab */}
        <TabsContent value="batch" className="mt-6">
          <BatchScenarioTester
            scenarios={scenarios}
            onScenariosChange={setScenarios}
            onRunAll={handleBatchRunAll}
            onRunSingle={handleRunSingle}
            isRunning={isBatchRunning}
            availableResources={availableResources}
            availableActions={availableActions}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
